import type {
  DatabaseRelationType,
  FieldType,
  RegistryFieldValueType,
  RegistryType,
} from "@/schemas/digital-files";
import type { Section } from "../types";
import type { ProccesedExpedientValues } from "@/types/entities/templates";
import { getDataCenterDataById } from "@/queries/data-center/data-center.api";

/**
 * Fetches values from data center based on relations
 * @param relations - Array of data center relations
 * @param entityField - The entity field configuration
 * @returns Array of fetched values (or display values as fallback)
 */
const fetchDataCenterValues = async (
  relations: Array<DatabaseRelationType>,
  entityField: FieldType,
): Promise<string[]> => {
  const fetchedValues: string[] = [];

  for (const relation of relations) {
    try {
      // Buscar en la configuración el nombre de la tabla y columna
      const tableName = entityField?.configuration?.options_root_table;
      const columnName = entityField?.configuration?.options_root_column;

      if (!tableName || !columnName) {
        // Si no hay configuración, usar displayValue como fallback
        if (relation.display_value) {
          fetchedValues.push(relation.display_value);
        }
        continue;
      }

      const relationId = relation.relation_id;
      const response = await getDataCenterDataById(tableName, relationId);

      if (
        response &&
        response.data &&
        Array.isArray(response.data) &&
        response.data.length > 0
      ) {
        const fetchedValue = response.data[0][columnName];
        fetchedValues.push(fetchedValue);
      }
    } catch (error) {
      console.error("Error fetching data center value:", error);
      // Si falla el fetch, usar displayValue como fallback
      if (relation.display_value) {
        fetchedValues.push(relation.display_value);
      }
    }
  }

  return fetchedValues;
};

export const processValues = async (data: RegistryType | undefined) => {
  const processed: ProccesedExpedientValues = {};

  // Recorrer field_values del registro
  if (data?.field_values && Array.isArray(data.field_values)) {
    for (const fieldValue of data.field_values) {
      const fieldId = fieldValue.expedient_base_entity_field_id;
      const entityField = fieldValue.entity_field;

      if (!fieldId || !entityField) continue;

      // Buscar relaciones de data center para este campo
      const dataCenterRelations =
        data.data_center_relations?.filter(
          (rel) =>
            rel.expedient_base_entity_field_id === fieldId &&
            rel.relation_type === "field_value",
        ) || [];

      let displayValues: unknown[] = [];

      // Si tiene fuente de datos (relaciones de data center)
      if (dataCenterRelations.length > 0) {
        displayValues = await fetchDataCenterValues(
          dataCenterRelations,
          entityField,
        );
      } else if (
        entityField.field_type?.toLowerCase() === "file" ||
        entityField.field_type?.toLowerCase() === "url"
      ) {
        // Si es un archivo, buscar el field y usar el valor de files
        const fieldRegistry = data?.field_values.find(
          (element) =>
            element.expedient_base_entity_field_id === entityField.id,
        );
        if (fieldRegistry) {
          displayValues = fieldRegistry.files || ["No Asignado"];
        } else {
          displayValues = ["No Asignado"];
        }
      } else {
        // No hay fuente de datos y no es un archivo, buscar en formattedValues
        const formattedValue = data.field_values_formatted?.[entityField.name];
        if (
          formattedValue !== null &&
          formattedValue !== undefined &&
          formattedValue !== ""
        ) {
          displayValues = [formattedValue];
        } else {
          // Si no hay valor, usar "No Asignado"
          displayValues = ["No Asignado"];
        }
      }

      // Buscar los options
      // Buscar relaciones de data center para este campo
      const dataCenterRelationsOptions =
        data.data_center_relations?.filter(
          (rel) =>
            rel.expedient_base_entity_field_id === fieldId &&
            rel.relation_type === "selected_option",
        ) || [];

      let optionsValues: string[] = [];
      if (dataCenterRelationsOptions.length > 0) {
        optionsValues = await fetchDataCenterValues(
          dataCenterRelationsOptions,
          entityField,
        );
      }

      processed[fieldId] = {
        value: displayValues,
        options: optionsValues,
      };
    }
  }
  return processed;
};

/**
 * Return the fields to save excluding file fields
 * key by the field registry id
 * @param sections
 * @param fieldValues
 * @returns
 */
export const getFieldsToSave = (
  sections: Section[],
  fieldValues: Record<string, unknown>,
  fieldValuesSource: RegistryFieldValueType[],
): Record<string, unknown> => {
  const fieldsWithoutFiles: Record<string, unknown> = {};

  for (const section of sections) {
    for (const field of section.fields) {
      const fieldValue = fieldValues[field.id];
      if (field.field_type === "SELECT" || field.field_type === "MULTISELECT") {
        // Transformar valores de data center
        const hasDataCenter =
          field.configuration?.options_root_table &&
          field.configuration?.options_root_column;

        if (
          fieldValue !== undefined &&
          fieldValue !== null &&
          fieldValue !== ""
        ) {
          if (field.field_type === "SELECT") {
            const val: { key: string; label: string; table?: string } =
              typeof fieldValue === "object"
                ? (fieldValue as { key: string; label: string; table?: string })
                : {
                    key: String(fieldValue),
                    label: "",
                    table: field.configuration?.options_root_table || "",
                  };
            if (hasDataCenter) {
              // SELECT con data center
              fieldsWithoutFiles[field.id] = {
                id: val.key,
                table: val.table,
                display: val.label,
                source: "data_center",
              };
            } else if (typeof val === "object" && val.key) {
              // SELECT con opciones locales - enviar solo el key
              fieldsWithoutFiles[field.id] = val.key;
            } else {
              // Ya es un string simple
              fieldsWithoutFiles[field.id] = val;
            }
          } else if (field.field_type === "MULTISELECT") {
            const vals = fieldValue as Array<{
              key: string;
              label: string;
              table?: string;
            }>;
            if (Array.isArray(vals) && vals.length > 0) {
              if (hasDataCenter) {
                // MULTISELECT con data center
                fieldsWithoutFiles[field.id] = vals.map((v) => ({
                  id: v.key,
                  table: field.configuration?.options_root_table || "",
                  display: v.label || "",
                  source: "data_center",
                }));
              } else if (typeof vals[0] === "object" && vals[0].key) {
                // MULTISELECT con opciones locales - enviar solo los keys
                fieldsWithoutFiles[field.id] = vals.map((v) => v.key);
              } else {
                // Ya son strings simples
                fieldsWithoutFiles[field.id] = vals;
              }
            }
          }
        }
      } else if (field.field_type === "URL") {
        const fieldRegistry = fieldValuesSource.find(
          (element) => element.expedient_base_entity_field_id === field.id,
        );
        if (!fieldRegistry) {
          // add an empty value to the fieldsWithoutFiles in order to create the registry field
          // for compatibility with old registries
          // new registries will be created and always be found
          fieldsWithoutFiles[field.id] = "";
        }
        // skip file fields
      } else {
        // filter empty values for other fields
        if (
          fieldValue !== undefined &&
          fieldValue !== null &&
          fieldValue !== ""
        ) {
          if (Array.isArray(fieldValue)) {
            fieldsWithoutFiles[field.id] = fieldValue;
          } else if (typeof fieldValue === "object") {
            fieldsWithoutFiles[field.id] = fieldValue;
          } else {
            fieldsWithoutFiles[field.id] = String(fieldValue);
          }
        }
      }
    }
  }
  return fieldsWithoutFiles;
};

/**
 * Return the file fields to save
 *
 * @param sections
 * @param fieldValues
 * @param fieldValuesSource
 * @returns
 */
export const getFilesToSave = (
  sections: Section[],
  fieldValues: Record<string, unknown>,
  fieldValuesSource: RegistryFieldValueType[],
): Array<{ fieldId: string; value: unknown }> => {
  const fileFields: Array<{ fieldId: string; value: unknown }> = [];
  for (const section of sections) {
    for (const field of section.fields) {
      const fieldValue = fieldValues[field.id];

      if (field.field_type === "URL") {
        // separate and upload file fields
        if (fieldValue !== undefined && fieldValue !== null) {
          const fieldRegistry = fieldValuesSource.find(
            (element) => element.expedient_base_entity_field_id === field.id,
          );
          if (fieldRegistry) {
            const hasNewFiles =
              fieldValue instanceof File ||
              (Array.isArray(fieldValue) &&
                fieldValue.some((f) => f instanceof File));

            if (hasNewFiles) {
              fileFields.push({
                fieldId: fieldRegistry.id,
                value: fieldValue,
              });
            }
          }
        }
      }
    }
  }
  return fileFields;
};

/**
 * Get files that need to be deleted
 * Compares current field values with original registry data
 * For single-file fields: Excludes files that are being replaced (when new File instance is present)
 * For multi-file fields: Deletes removed files even when adding new ones
 * @param sections
 * @param fieldValues - Current field values
 * @param registryData - Original registry data
 * @returns Array of file IDs to delete
 */
export const getFilesToDelete = (
  sections: Section[],
  fieldValues: Record<string, unknown>,
  registryData?: RegistryType,
): string[] => {
  if (!registryData?.field_values) return [];

  const filesToDelete: string[] = [];

  for (const section of sections) {
    for (const field of section.fields) {
      if (field.field_type === "URL") {
        const currentValue = fieldValues[field.id];
        const originalFieldValue = registryData.field_values.find(
          (fv) => fv.expedient_base_entity_field_id === field.id,
        );

        if (
          originalFieldValue?.files &&
          Array.isArray(originalFieldValue.files)
        ) {
          const allowMultipleFiles =
            field.configuration?.allowMultipleFiles ?? false;

          // Check if there's a replacement (new File being uploaded)
          const hasNewFile =
            currentValue instanceof File ||
            (Array.isArray(currentValue) &&
              currentValue.some((f) => f instanceof File));

          // For single-file fields: if there's a new file, it's a replacement - don't delete the original
          // For multi-file fields: always check and delete removed files
          if (!allowMultipleFiles && hasNewFile) {
            continue;
          }

          const currentFiles = Array.isArray(currentValue) ? currentValue : [];

          // Find files that were in original but are not in current
          for (const originalFile of originalFieldValue.files) {
            const stillExists = currentFiles.some(
              (currentFile) =>
                typeof currentFile === "object" &&
                "id" in currentFile &&
                currentFile.id === originalFile.id,
            );

            if (!stillExists && originalFile.id) {
              filesToDelete.push(originalFile.id);
            }
          }
        }
      }
    }
  }

  return filesToDelete;
};

// ===== CHANGE DETECTION HELPERS =====

/**
 * Normalizes any value to a string for consistent comparison.
 * Handles special cases like arrays, objects, null, undefined, etc.
 */
export const normalizeValueForComparison = (val: unknown): string => {
  // Empty values are normalized to empty string
  if (val === null || val === undefined || val === "") return "";

  // Arrays
  if (Array.isArray(val)) {
    if (val.length === 0) return "";

    // Extract relevant values from objects inside the array
    const normalized = val
      .map((v) => {
        if (typeof v === "object" && v !== null && "id" in v) {
          return (v as { id: string }).id;
        }
        if (typeof v === "object" && v !== null && "key" in v) {
          return (v as { key: string }).key;
        }
        return v;
      })
      .sort();

    return JSON.stringify(normalized);
  }

  // Objects
  if (typeof val === "object" && val !== null) {
    if ("value" in val) return String((val as { value?: string }).value || "");
    if ("id" in val) return String((val as { id?: string }).id || "");
    return JSON.stringify(val);
  }

  // Primitives (string, number, boolean)
  return String(val).trim();
};

/**
 * Gets the default value of a field based on its configuration.
 * Used in creation mode to compare initial values.
 */
export const getFieldDefaultValue = (field: FieldType) => {
  if (field.default_value === null || field.default_value === undefined) {
    return "";
  }

  // Identification field has special structure
  if (field.configuration?.type === "identification") {
    return { value: field.default_value };
  }

  // Multiselect returns array of {key, label} objects
  if (field.field_type === "MULTISELECT") {
    return [
      {
        key: field.default_value,
        label: field.default_value,
      },
    ];
  }

  return field.default_value;
};

/**
 * Finds the initial value of a field in saved data using the field name.
 * Important: We use name instead of ID because IDs can change.
 */
export const findInitialFieldValueByName = (
  fieldName: string,
  fieldType: string,
  registryData: Partial<RegistryType> | null | undefined,
): unknown => {
  if (!registryData || !registryData.field_values) return "";

  // Find the field_value that corresponds to this field name
  // Cast to Partial<RegistryFieldValueType>[] to handle partial mocks in tests (Partial is not deep)
  const initialValueObj = (
    registryData.field_values as Partial<RegistryFieldValueType>[]
  ).find((fv) => fv.entity_field?.name === fieldName);

  if (!initialValueObj) return "";

  // URL type fields store files
  if (fieldType === "URL") {
    return initialValueObj.files || [];
  }

  // Multiselect can be stored as JSON string or array
  if (fieldType === "MULTISELECT") {
    try {
      const parsed =
        typeof initialValueObj.value === "string" && initialValueObj.value
          ? JSON.parse(initialValueObj.value)
          : initialValueObj.value;
      return parsed || [];
    } catch {
      return initialValueObj.value ? [initialValueObj.value] : [];
    }
  }

  return initialValueObj.value ?? "";
};

/**
 * Finds the selected Data Center option for a field.
 * Uses the field name to find the corresponding relation.
 */
export const findInitialDataCenterOptionByName = (
  fieldName: string,
  registryData: Partial<RegistryType> | null | undefined,
): string => {
  if (
    !registryData ||
    !registryData.field_values ||
    !registryData.data_center_relations
  )
    return "";

  // First find the field_value to get its ID
  // Cast to Partial<RegistryFieldValueType>[] to handle partial mocks in tests (Partial is not deep)
  const fieldValue = (
    registryData.field_values as Partial<RegistryFieldValueType>[]
  ).find((fv) => fv.entity_field?.name === fieldName);

  if (!fieldValue) return "";

  // Find the Data Center relation using the field_value ID
  const dataCenterRel = (
    registryData.data_center_relations as Partial<DatabaseRelationType>[]
  ).find(
    (rel) =>
      rel.expedient_base_entity_field_id ===
        fieldValue.expedient_base_entity_field_id &&
      rel.relation_type === "selected_option",
  );

  return dataCenterRel?.relation_id || "";
};

/**
 * Compares two arrays of IDs in a sorted manner.
 * Useful for comparing lists of users or related registries.
 */
export const compareIdArrays = (arr1: string[], arr2: string[]): boolean => {
  const sorted1 = [...arr1].sort();
  const sorted2 = [...arr2].sort();
  return JSON.stringify(sorted1) === JSON.stringify(sorted2);
};
